import { useEffect, useMemo, useRef, useState } from 'react';

import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import TestAgentButton from '@/components/atoms/Button/TestAgentButton/TestAgentButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  MODAL_CONFIRM_CHANGES,
  MODAL_TRYIT,
  MODAL_WHATSAPP_TEST,
} from '@/components/organisms/Modals/ModalConductor';
import { hasChanges } from '@/components/pages/Versions/TimelineBox';
import { useVersionsTable } from '@/components/pages/Versions/useVersionsTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShortcutHint } from '@/components/ui/kbd';
import { SHORTCUTS } from '@/constants/shortcuts';
import useBrains from '@/hooks/useBrains';
import useDesks from '@/hooks/useDesks';
import useDialogs from '@/hooks/useDialogs';
import useEntities from '@/hooks/useEntities';
import useIntents from '@/hooks/useIntents';
import useVersions from '@/hooks/useVersions';
import { useBillingState } from '@/modules/billing/hooks/useBillingState';
import { selectIntentDraft } from '@/redux/expressions/selectors';
import { pushModal } from '@/redux/modals/actions';
import { selectIsTryItModalOpen } from '@/redux/modals/selectors';
import { selectIsDirty } from '@/redux/nodes/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId, selectDialogId } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

export const HeaderActions = () => {
  const [isTestMenuOpen, setIsTestMenuOpen] = useState(false);

  // Redux selectors
  const brainId = useSelector(selectBrainId);
  const dialogId = useSelector(selectDialogId);
  const intentName = useSelector(selectIntentDraft, shallowEqual);
  const isDirty = useSelector(selectIsDirty);
  const canWriteBrains = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const canWriteRules = useSelector((state: RootState) =>
    getPermissions(state, 'rules', actions.WRITE)
  );

  const isTryItModalOpen = useSelector(selectIsTryItModalOpen);

  // Custom hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { brain } = useBrains(brainId);
  const billingState = useBillingState();
  const { entityName } = useParams();
  const { saveIntent } = useIntents(brainId, intentName);
  const { saveEntity } = useEntities(brainId, entityName);
  const { handleCreateVersion } = useVersionsTable();
  const { saveDialog } = useDialogs(brainId, dialogId);
  const { desks } = useDesks();
  const { differences } = useVersions({ brainId, enableDiff: true });

  // Local variables
  const hasPlanExpired = billingState?.has_expired;
  const hasWhatsApp = useMemo(
    () =>
      !!desks?.find((desk) =>
        desk?.integrations?.some((integration) => integration.type === 'whatsapp')
      ),
    [desks]
  );

  useEffect(() => {
    if (!hasWhatsApp) {
      setIsTestMenuOpen(false);
    }
  }, [hasWhatsApp]);

  const hasUnpublishedChanges = useMemo(
    () => (differences ? hasChanges(differences) : false),
    [differences]
  );

  // Only show publish button for users who can both edit AND publish
  const canPublish = canWriteBrains && canWriteRules;
  const isPublishDisabled = brain?.status !== 'available';

  // Handlers
  const warningModal = async () => {
    return new Promise<void>((resolve, reject): void => {
      const onSave = async () => {
        try {
          if (dialogId) {
            await saveDialog();
          } else if (intentName) {
            await saveIntent();
          } else if (entityName) {
            await saveEntity();
          }
          // This will push the resolve to the end of the event loop,
          // so the modal will be closed before the promise is resolved.
          setTimeout(() => {
            resolve();
          }, 0);
        } catch {
          reject();
        }
      };
      dispatch(
        pushModal(MODAL_CONFIRM_CHANGES, {
          onSave,
          onDiscard: resolve,
          secondaryText: t('dialog.warningModal.secondary_try_it'),
          primaryText: t('dialog.warningModal.primary_try_it'),
        })
      );
    });
  };

  const handlePublish = async () => {
    if (isDirty) {
      try {
        await warningModal();
      } catch {
        return; // User cancelled
      }
    }
    handleCreateVersion();
  };

  const showTryItModal = async () => {
    if (isDirty) {
      try {
        await warningModal();
      } catch {
        return;
      }
    }
    dispatch(pushModal(MODAL_TRYIT, { brainId }));
  };

  const showWhatsAppTestModal = async () => {
    if (isDirty) {
      try {
        await warningModal();
      } catch {
        return;
      }
    }
    dispatch(pushModal(MODAL_WHATSAPP_TEST, { brainId }));
  };

  const handleTestShortcut = (e: KeyboardEvent) => {
    if (e.repeat) {
      return;
    }
    e.preventDefault();
    if (!canWriteBrains || hasPlanExpired) {
      return;
    }
    showTryItModal();
  };

  // Use refs so keyboard shortcuts always call the latest handler,
  // avoiding stale closures over save functions and form data.
  const handlePublishRef = useRef(handlePublish);
  handlePublishRef.current = handlePublish;

  const handleTestShortcutRef = useRef(handleTestShortcut);
  handleTestShortcutRef.current = handleTestShortcut;

  // Keyboard shortcuts
  useHotkeys(
    SHORTCUTS.test,
    (e: KeyboardEvent) => {
      if (isTryItModalOpen) {
        return;
      }
      handleTestShortcutRef.current(e);
    },
    { enableOnFormTags: false },
    [isTryItModalOpen]
  );

  useHotkeys(
    SHORTCUTS.publish,
    (e: KeyboardEvent) => {
      if (e.repeat || isTryItModalOpen) {
        return;
      }
      e.preventDefault();
      if (!canPublish || isPublishDisabled) {
        return;
      }
      handlePublishRef.current();
    },
    { enableOnFormTags: false },
    [canPublish, isPublishDisabled, isTryItModalOpen]
  );

  // Tooltip contents
  const testTooltipContent = hasPlanExpired ? (
    t('brain.expired_plan')
  ) : (
    <span className="inline-flex items-center gap-1.5">
      {t('common.test_shortcut')} <ShortcutHint shortcut={SHORTCUTS.test} />
    </span>
  );

  const publishTooltipContent = hasUnpublishedChanges ? (
    t('versions.unpublished_changes')
  ) : (
    <span className="inline-flex items-center gap-1.5">
      {t('versions.publish_shortcut')} <ShortcutHint shortcut={SHORTCUTS.publish} />
    </span>
  );

  return (
    <div className="flex items-center gap-2">
      <InfoTooltip
        content={testTooltipContent}
        size={hasPlanExpired ? 'large' : 'default'}
        open={isTestMenuOpen ? false : undefined}
      >
        <span>
          <TestAgentButton
            disabled={!canWriteBrains || hasPlanExpired}
            hasWhatsApp={hasWhatsApp}
            onClickMoveo={showTryItModal}
            onClickWhatsApp={showWhatsAppTestModal}
            onOpenChange={setIsTestMenuOpen}
          />
        </span>
      </InfoTooltip>

      {canPublish && (
        <InfoTooltip content={publishTooltipContent}>
          <div className="relative">
            <Button
              variant="primary"
              size="sm"
              disabled={isPublishDisabled}
              onClick={handlePublish}
            >
              {t('versions.publish')}
            </Button>

            {hasUnpublishedChanges && (
              <Badge
                type="bordered"
                flavour="destructive"
                size="md"
                className="absolute -top-1 -right-1 shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_-1px_rgba(0,0,0,0.10)]"
                aria-label={t('versions.unpublished_changes')}
              />
            )}
          </div>
        </InfoTooltip>
      )}
    </div>
  );
};
